import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  cn,
  calculateShapeArea,
  formatPersistedAreaForDisplay,
} from '@ais-ui/utils';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { Button } from '@ais-ui/ui-core';
import { PiFloppyDisk, PiX } from 'react-icons/pi';
import { useShapesEdit } from '@ais-ui/api';
import { useToast } from '@ais-ui/ui-core';

interface ShapeAreaPopupProps {
  shape: GeoJson;
  position: { x: number; y: number };
  isOwner: boolean;
  onClose: () => void;
}

export const ShapeAreaPopup = ({
  shape,
  position,
  isOwner,
  onClose,
}: ShapeAreaPopupProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { mutate: editShape, isPending } = useShapesEdit();
  const [isSaved, setIsSaved] = useState(false);

  // Calculate area
  const areaResult = useMemo(() => {
    return calculateShapeArea(shape.geometry, shape.properties?.radius);
  }, [shape]);

  // Check if area is already saved
  const existingArea = shape.properties?.area;

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleSaveArea = () => {
    if (!areaResult || !shape.properties?.id) return;

    const updatedShape: GeoJson = {
      ...shape,
      properties: {
        ...shape.properties,
        area: areaResult.formatted,
      },
    };

    editShape(
      { id: shape.properties.id, json: updatedShape },
      {
        onSuccess: () => {
          setIsSaved(true);
          toast({
            title: 'Area saved',
            description: `Area (${areaResult.displayFormatted}) has been saved to the shape.`,
          });
        },
        onError: () => {
          toast({
            title: 'Error',
            description: 'Failed to save area to shape.',
            variant: 'destructive',
          });
        },
      },
    );
  };

  if (!areaResult) {
    return null;
  }

  const popupContent = (
    <div
      ref={popupRef}
      className={cn(
        'fixed z-50 min-w-[14rem] overflow-hidden rounded-md border bg-popover p-3 text-popover-foreground shadow-md',
        'animate-in fade-in-0 zoom-in-95',
      )}
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold">
          {shape.properties?.name || 'Shape'} Area
        </span>
        <Button
          variant="ghost"
          size="smIcon"
          onClick={onClose}
          className="h-6 w-6"
        >
          <PiX className="h-4 w-4" />
        </Button>
      </div>

      <div className="text-2xl font-bold text-primary mb-3">
        {areaResult.displayFormatted}
      </div>

      {existingArea && (
        <div className="text-xs text-muted-foreground mb-2">
          Saved area: {formatPersistedAreaForDisplay(existingArea)}
        </div>
      )}

      {isOwner && !isSaved && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleSaveArea}
          disabled={isPending}
          className="w-full"
        >
          <PiFloppyDisk className="h-4 w-4 mr-2" />
          {isPending ? 'Saving...' : 'Save to Shape'}
        </Button>
      )}

      {isSaved && (
        <div className="text-sm text-green-600 dark:text-green-400 text-center">
          ✓ Area saved
        </div>
      )}

      {!isOwner && (
        <div className="text-xs text-muted-foreground text-center">
          Only the shape owner can save the area
        </div>
      )}
    </div>
  );

  return createPortal(popupContent, document.body);
};
